import axios from "axios";
import { getEnv } from "./get_env";
import { showSessionExpiredModal } from "./session_expired_modal";
import cookieStorage from "./cookie_storage";

const env = getEnv();

// Main Axios instance
const instance = axios.create({
  baseURL: env.apiURL,
  headers: {
    audience: "FILENOWCLIENTID",
  },
});

// Separate Axios instance for refresh token requests
const refreshInstance = axios.create({
  baseURL: env.apiURL,
  headers: {
    audience: "FILENOWCLIENTID",
  },
});

// Request interceptor to attach access token
instance.interceptors.request.use(
  (config) => {
    const token = cookieStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

// Function to process queued requests after refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor
instance.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 errors and avoid retry loops
    if (error?.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request until refresh finishes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = token;
            return instance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = cookieStorage.getItem("refresh_token");
        if (!refreshToken) {
          // No refresh token, force logout
          throw new Error("No refresh token available");
        }

        // Use separate instance to avoid interceptor loop
        const response = await refreshInstance.post("/auth/refresh-token", { refreshToken });
        const newAccessToken = response?.data?.accessToken;
        const newRefreshToken = response?.data?.refreshToken;

        if (!newAccessToken || !newRefreshToken) {
          throw new Error("Invalid refresh response");
        }

        // Store new tokens
        cookieStorage.setItem("token", newAccessToken);
        cookieStorage.setItem("refresh_token", newRefreshToken);

        // Update default headers
        instance.defaults.headers.common["Authorization"] = newAccessToken;

        processQueue(null, newAccessToken);

        // Retry original request
        originalRequest.headers["Authorization"] = newAccessToken;
        return instance(originalRequest);
      } catch (err) {
        processQueue(err, null);
        showSessionExpiredModal(()=> {}, 0)
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
